import { GeneralTooltip } from '@components/common/GeneralTooltip';
import MaterialIcon from '@components/common/MaterialIcon';
import { markdownToPreview } from '@components/designer/utils/markdownToPreview';
import ButtonT from '@components/tailwind-components/Button/ButtonT';
import DropDownSearchInput from '@components/tailwind-components/Dropdown/DropDownSearchInput';
import CheckboxTogglerT from '@components/tailwind-components/Input/CheckboxTogglerT';
import { InputT } from '@components/tailwind-components/Input/InputT';
import TextareaT from '@components/tailwind-components/Input/TextareaT';
import LabelT from '@components/tailwind-components/Label/LabelT';
import { URL_WITH_PROTOCOL_REGEX } from '@constants/regex';
import {
  LLMUsageCheckResultStatus,
  STATUSES,
  URL_REMOVAL_FLAG,
} from '@conversed/rest/src/contracts/llm-usages/check-results';
import {
  DEFAULT_REPORT_ISSUE,
  DEFAULT_REPORT_ISSUE_PARAMS,
  DEFAULT_SAVE_ANSWER_AS_TEMPLATE,
  DEFAULT_USE_FOR_AI_KNOWLEDGE_DATA,
  type TConversationDetailsSchema,
  type TConversationsResponse,
} from '@conversed/rest/src/contracts/llm-usages/llm-usages.contract';
import type { TReportIssueParams } from '@conversed/rest/src/contracts/llm-usages/report-issue';
import { TRANSLATION_LANGUAGE_NAMES } from '@conversed/shared/src/central/constants/languages';
import { PERMISSIONS } from '@conversed/shared/src/central/constants/permissions';
import ErrorCodes from '@conversed/shared/src/errors/error-codes';
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react';
import { formatDate } from '@helpers/dateHelpers';
import { purify } from '@helpers/domHelper';
import { getHotTopicDisplayName } from '@helpers/hotTopicHelper';
import { isDutchText } from '@helpers/languageDetectionHelper';
import { toastErrorTranslated, toastInfoTranslated } from '@helpers/toast';
import { updateCheckResultFieldInState } from '@helpers/trainAIHelper';
import { useLLMUsagesActions } from '@hooks/llmUsages/useLLMUsagesActions';
import { useModal } from '@hooks/modal/useModal';
import { useTemplateResponseActions } from '@hooks/settings/template-response/useTemplateResponseActions';
import { useTemplateResponseInit } from '@hooks/settings/template-response/useTemplateResponseInit';
import { useTemplateResponses } from '@hooks/settings/template-response/useTemplateResponses';
import { useHotTopicActions } from '@hooks/train-ai/useHotTopicActions';
import { usePermissions } from '@hooks/usePermissions';
import { usePlatformFeatures } from '@hooks/usePlatformFeatures';
import person from '@images/emojiis-ios/person.svg';
import parse from 'html-react-parser';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IntentRedirectLabel } from '../IntentRedirectLabel';
import { FlagEmoji } from '../utils/FlagEmoji';
import { iconsHelper } from '../utils/iconsHelper';
import { AnswerEditor, type AnswerEditorHandle } from './AnswerEditor';
import FactCheckingReportIssueSection from './FactCheckingReportIssueSection';
import FactCheckingStatusSection from './FactCheckingStatusSection';
import type { TFactCheckingModalProps } from './fact-checking-modal.type';

export const FactCheckingModal: React.FC<TFactCheckingModalProps> = ({
  data,
  newRequestedStatus,
  updatedConversation = () => {},
  onTranslated,
  callback,
  isReoptimization,
  preservedState,
  llmBlockId,
  requestedHotTopicId,
}) => {
  const { t } = useTranslation();
  const { hasPermissions } = usePermissions();
  const { hasMultilingualTrainAI } = usePlatformFeatures();
  const { hideModal, showModal } = useModal();
  const { handleViewRelatedConversations } = useHotTopicActions();
  const { updateCheckResult, translateConversation } = useLLMUsagesActions();
  const { createTemplateResponse } = useTemplateResponseActions();
  const [conversations, setConversations] =
    useState<TConversationsResponse>(data);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [localPreservedState, setLocalPreservedState] =
    useState(preservedState);
  const currentConversation = useMemo(
    () => conversations?.results?.[currentIndex],
    [currentIndex, conversations?.results],
  );
  // An Intent Check sent this question to a flow the A.I. does not answer: read-only, nothing to review.
  const isCurrentConversationIntentRedirect =
    currentConversation?.isIntentRedirect === true;
  const isCurrentConversationDutch = useMemo(
    () =>
      isDutchText(currentConversation?.question) ||
      isDutchText(currentConversation?.answer),
    [currentConversation?.question, currentConversation?.answer],
  );
  const [status, setStatus] = useState<LLMUsageCheckResultStatus | ''>(
    newRequestedStatus || currentConversation?.checkResult?.status || '',
  );
  const [correctedAnswer, setCorrectedAnswer] = useState(
    currentConversation?.checkResult?.correctedAnswer || '',
  );
  const [correctedURL, setCorrectedURL] = useState('');
  const [shouldDisplayCorrectionSection, setShouldDisplayCorrectionSection] =
    useState(false);
  const [reportIssue, setReportIssue] = useState(DEFAULT_REPORT_ISSUE);
  const [reportIssueParams, setReportIssueParams] =
    useState<TReportIssueParams>(DEFAULT_REPORT_ISSUE_PARAMS);
  const [isUserInteraction, setIsUserInteraction] = useState(false); // Track user interaction with the report issue section

  const [useForAIKnowledgeData, setUseForAIKnowledgeData] = useState(
    DEFAULT_USE_FOR_AI_KNOWLEDGE_DATA,
  );
  const [saveAnswerAsTemplate, setSaveAnswerAsTemplate] = useState(
    DEFAULT_SAVE_ANSWER_AS_TEMPLATE,
  );
  const [correctionIsMandatory, setCorrectionIsMandatory] = useState(false);
  const answerEditorRef = useRef<AnswerEditorHandle>(null);
  const [templateName, setTemplateName] = useState('');
  const [clearTrigger, setClearTrigger] = useState(0);
  const isNavigatingToAnotherModal = useRef(false);
  const hasAppliedPreservedStateRef = useRef(false);

  const [translationState, setTranslationState] = useState<{
    sourceLanguageName: string;
    sourceLang: string;
    translatedQuestion: string;
    translatedAnswer: string;
  } | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [showTranslated, setShowTranslated] = useState(false);
  // Saves the correctedAnswer text before switching to translated mode so it can be restored on toggle-back
  const originalCorrectedAnswerRef = useRef<string>('');
  // Saves the Dutch correction when toggling back to original so it can be restored on re-toggle
  const dutchCorrectionRef = useRef<string>('');
  // Tracks the live active conversation ID so async translate callbacks can guard against stale closures
  const activeConversationIdRef = useRef<string | undefined>(
    currentConversation?._id,
  );

  // we should only fetch template responses if we're not using preserved state
  useTemplateResponseInit(true, !localPreservedState);

  const { templateResponses } = useTemplateResponses();

  const templateResponseValues = useMemo(() => {
    return templateResponses.map((response) => ({
      value: response._id.toString(),
      description: response.name,
    }));
  }, [templateResponses]);

  const templateOptions = useMemo(() => {
    return templateResponses.map((response) => ({
      value: response._id.toString(),
      description: response.name,
      content: response.content || '',
      url: response.url || '',
    }));
  }, [templateResponses]);

  const getCorrectedAnswer = useCallback(
    (status: LLMUsageCheckResultStatus | '') => {
      const currentConversation = conversations?.results?.[currentIndex];

      if (status === LLMUsageCheckResultStatus.NEEDS_IMPROVEMENT) {
        // When the user selects the "Needs Improvement" fact-check option, the AI-generated answer text should automatically appear in the "Write your correction answer here" field, if the user didn't provide any correction text before.
        // This allows the user to easily edit the text without having to manually copy ( from Answer section ) and paste it ( to "Write your correction answer here" section ).
        // When a Dutch translation is cached, prefer it so the reviewer
        // edits in Dutch (the editing language) instead of the source language.
        return (
          localPreservedState?.correctedAnswer?.trim() ||
          currentConversation?.checkResult?.correctedAnswer?.trim() ||
          (hasMultilingualTrainAI &&
            currentConversation?.translation?.answer) ||
          currentConversation?.answer
        );
      }

      return '';
    },
    [currentIndex, conversations, localPreservedState, hasMultilingualTrainAI],
  );

  const getCorrectedURL = useCallback(
    (status: LLMUsageCheckResultStatus | '') => {
      const currentConversation = conversations?.results?.[currentIndex];
      if (status === LLMUsageCheckResultStatus.NEEDS_IMPROVEMENT) {
        // When the user selects the "Needs Improvement" fact-check option, the AI-generated URL should automatically appear in the "Write your correction URL here" field if the user didn't provide any correction URL before.
        // This allows the user to easily edit the URL without having to manually copy ( from URL section ) and paste it ( to "Write your correction URL here" section ).
        // If the user removed the URL from the "Write your correction URL here" field, we should set the correctedURL to the URL_REMOVAL_FLAG.
        // This means that the user doesn't want to use the URL anymore for this response.
        return (
          localPreservedState?.correctedURL?.trim() ||
          currentConversation?.checkResult?.correctedURL?.trim() ||
          currentConversation?.url
        );
      }

      return '';
    },
    [currentIndex, conversations, localPreservedState],
  );

  useEffect(() => {
    const initStatus =
      localPreservedState?.status ||
      newRequestedStatus ||
      conversations?.results?.[currentIndex]?.checkResult?.status ||
      '';

    const initCorrectedAnswer = getCorrectedAnswer(initStatus);

    setStatus(initStatus);
    setCorrectedAnswer(initCorrectedAnswer);

    if (answerEditorRef.current && initCorrectedAnswer) {
      answerEditorRef.current.setEditorText(initCorrectedAnswer);
    }

    setCorrectedURL(getCorrectedURL(initStatus));
    setUseForAIKnowledgeData(
      localPreservedState?.useForAIKnowledgeData ??
        conversations?.results?.[currentIndex]?.checkResult
          ?.useForAIKnowledgeData ??
        DEFAULT_USE_FOR_AI_KNOWLEDGE_DATA,
    );
    setReportIssue(
      localPreservedState?.reportIssue ??
        conversations?.results?.[currentIndex]?.checkResult?.reportIssue ??
        DEFAULT_REPORT_ISSUE,
    );
    setReportIssueParams(
      localPreservedState?.reportIssueParams ??
        conversations?.results?.[currentIndex]?.checkResult
          ?.reportIssueParams ??
        DEFAULT_REPORT_ISSUE_PARAMS,
    );

    if (localPreservedState) {
      setCurrentIndex(localPreservedState.currentIndex);
      setSaveAnswerAsTemplate(localPreservedState.saveAnswerAsTemplate);
      setTemplateName(localPreservedState.templateName);

      isNavigatingToAnotherModal.current = false;
      hasAppliedPreservedStateRef.current = true;

      // Clean up the preserved state after applying it
      setLocalPreservedState(undefined);
    }
  }, [
    currentIndex,
    conversations,
    newRequestedStatus,
    getCorrectedAnswer,
    getCorrectedURL,
    localPreservedState,
  ]);

  useEffect(() => {
    setShouldDisplayCorrectionSection(
      status === LLMUsageCheckResultStatus.NEEDS_IMPROVEMENT,
    );
  }, [status]);

  // * reset setCorrectionIsMandatory when switching conversation
  useEffect(() => {
    if (currentConversation) {
      setCorrectionIsMandatory(false);
    }
  }, [currentConversation]);

  // Initialise translation state from cached fields on each conversation change.
  // If a cached translation exists, default to the Dutch view; reviewer can
  // toggle back to the original via the flag. No Bedrock call here.
  useEffect(() => {
    activeConversationIdRef.current = currentConversation?._id;
    const cached = currentConversation?.translation;
    if (cached) {
      setTranslationState({
        sourceLanguageName:
          TRANSLATION_LANGUAGE_NAMES[cached.sourceLang] ?? cached.sourceLang,
        sourceLang: cached.sourceLang,
        translatedQuestion: cached.question,
        translatedAnswer: cached.answer,
      });
    } else {
      setTranslationState(null);
    }
    originalCorrectedAnswerRef.current = '';
    dutchCorrectionRef.current = '';
    setShowTranslated(hasMultilingualTrainAI && !!cached);
    setIsTranslating(false);
  }, [currentConversation, hasMultilingualTrainAI]);

  const switchToTranslated = useCallback(
    (dutchAnswer: string) => {
      // Only capture original text on the first toggle; re-toggles should not overwrite it
      if (!showTranslated) {
        originalCorrectedAnswerRef.current = correctedAnswer;
      }
      const savedDutch = dutchCorrectionRef.current;
      dutchCorrectionRef.current = '';
      if (savedDutch?.trim()) {
        // Reviewer had typed a Dutch correction before toggling back; restore it
        setCorrectedAnswer(savedDutch);
        answerEditorRef.current?.setEditorText(savedDutch);
      } else if (
        !correctedAnswer?.trim() ||
        correctedAnswer === currentConversation?.answer
      ) {
        // Auto-fill with machine translation if the reviewer hasn't typed anything
        setCorrectedAnswer(dutchAnswer);
        answerEditorRef.current?.setEditorText(dutchAnswer);
      }
      setShowTranslated(true);
    },
    [correctedAnswer, currentConversation?.answer, showTranslated],
  );

  const handleTranslate = useCallback(async () => {
    if (!currentConversation || currentConversation.isIntentRedirect) {
      return;
    }
    if (translationState) {
      if (showTranslated) {
        // Save Dutch correction before restoring original-language text
        dutchCorrectionRef.current = correctedAnswer;
        const restored =
          originalCorrectedAnswerRef.current ||
          currentConversation?.checkResult?.correctedAnswer ||
          currentConversation?.answer ||
          '';
        setCorrectedAnswer(restored);
        answerEditorRef.current?.setEditorText(restored);
        setShowTranslated(false);
      } else {
        switchToTranslated(translationState.translatedAnswer);
      }
      return;
    }
    const conversationId = currentConversation._id;
    setIsTranslating(true);
    try {
      const result = await translateConversation(
        currentConversation._id,
        currentConversation.question,
        currentConversation.answer,
      );
      if (result && conversationId === activeConversationIdRef.current) {
        setTranslationState({
          sourceLanguageName: result.sourceLanguageName,
          sourceLang: result.sourceLang,
          translatedQuestion: result.translatedQuestion,
          translatedAnswer: result.translatedAnswer,
        });
        const translation = {
          question: result.translatedQuestion,
          answer: result.translatedAnswer,
          sourceLang: result.sourceLang,
        };
        // Backend already cached the translation; mirror it into the modal's
        // local list and the parent list so the weekly overview shows the
        // translated text without needing a page refresh.
        setConversations((previous) => ({
          ...previous,
          results: previous.results.map((item) =>
            item._id === conversationId ? { ...item, translation } : item,
          ),
        }));
        onTranslated?.(conversationId, translation);
        switchToTranslated(result.translatedAnswer);
      }
    } finally {
      setIsTranslating(false);
    }
  }, [
    translationState,
    showTranslated,
    correctedAnswer,
    currentConversation,
    translateConversation,
    switchToTranslated,
    onTranslated,
  ]);

  const hasChanges = useCallback(
    (
      currentConversation: TConversationDetailsSchema,
      newValues: {
        status: LLMUsageCheckResultStatus | '';
        correctedAnswer: string;
        correctedURL: string;
        reportIssue: boolean;
        reportIssueParams?: TReportIssueParams;
        useForAIKnowledgeData: boolean;
        newRequestedStatus?: LLMUsageCheckResultStatus;
      },
    ) => {
      const previousCheckResult = currentConversation?.checkResult;

      if (!previousCheckResult) {
        return true; // If there's no previous check result, we need to send the request
      }

      // Check if reportIssueParams differ only if newValues.reportIssue is true
      const hasReportIssueParamsChanges =
        newValues.reportIssue &&
        JSON.stringify(previousCheckResult.reportIssueParams || {}) !==
          JSON.stringify(newValues.reportIssueParams || {});

      return (
        previousCheckResult.status !== newValues.status ||
        previousCheckResult.correctedAnswer !== newValues.correctedAnswer ||
        previousCheckResult.correctedURL !== newValues.correctedURL ||
        previousCheckResult.reportIssue !== newValues.reportIssue ||
        hasReportIssueParamsChanges ||
        previousCheckResult.useForAIKnowledgeData !==
          newValues.useForAIKnowledgeData ||
        (newValues.newRequestedStatus !== undefined &&
          previousCheckResult.status !== newValues.newRequestedStatus) ||
        hasAppliedPreservedStateRef.current
      );
    },
    [],
  );

  const handleCorrectedAnswerChange = useCallback(
    (value: string) => {
      if (correctionIsMandatory && value?.trim()?.length) {
        setCorrectionIsMandatory(false);
      }
      // Convert Markdown to HTML, ex: convert [text](url) to HTML links
      const htmlValue = markdownToPreview(value);
      setCorrectedAnswer(htmlValue);
    },
    [correctionIsMandatory],
  );

  const handleCorrectedURLChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      // if the corrected URL is empty, set the correctedURL to the URL_REMOVAL_FLAG
      // this means that the user doesn't want to use the URL anymore for this response
      setCorrectedURL(event.target?.value?.trim() || URL_REMOVAL_FLAG);

      if (correctionIsMandatory) {
        setCorrectionIsMandatory(false);
      }
    },
    [correctionIsMandatory],
  );

  const handleStatusChange = useCallback(
    async (status: LLMUsageCheckResultStatus) => {
      if (isCurrentConversationIntentRedirect) {
        return;
      }
      setStatus(status);

      switch (status) {
        case LLMUsageCheckResultStatus.NEEDS_IMPROVEMENT:
          // If the selected status is Needs Improvement, the correctedAnswer field should be auto-populated with the AI-generated answer text, if the user didn't provide any correction text before.
          setCorrectedAnswer(getCorrectedAnswer(status));
          setCorrectedURL(getCorrectedURL(status));
          // 'Use for A.I. knowledge data' should auto switch on here too, same as Good - corrected answers feed back into the dataset.
          setUseForAIKnowledgeData(true);
          break;
        case LLMUsageCheckResultStatus.GOOD:
          // If the selected status is Good, ‘Use for A.I. knowledge data = auto switched on > this ensures that positively marked responses are put back into the dataset
          setUseForAIKnowledgeData(true);
          setCorrectionIsMandatory(false);
          break;
        case LLMUsageCheckResultStatus.IRRELEVANT:
          // If the status is irrelevant, don't use it for AI knowledge data.
          setUseForAIKnowledgeData(false);
          setCorrectionIsMandatory(false);
          break;
      }
    },
    [isCurrentConversationIntentRedirect, getCorrectedAnswer, getCorrectedURL],
  );

  const scrollToReportIssueSection = useCallback(() => {
    const reportIssueSection = document.getElementById('report-issue-section');
    if (reportIssueSection) {
      reportIssueSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, []);

  const validateNeedsImprovement = useCallback(() => {
    // If the status is needs_improvement, the correction answer is required.
    if (!correctedAnswer?.trim()) {
      setCorrectionIsMandatory(true);
      throw new Error(
        t('train-ai.fact-checking.error.corrected-answer-required'),
      );
    }

    // If the status is needs_improvement and the corrected URL is not empty, the URL should be a valid URL.
    if (
      correctedURL?.trim() &&
      correctedURL !== URL_REMOVAL_FLAG &&
      !URL_WITH_PROTOCOL_REGEX.test(correctedURL.trim())
    ) {
      setCorrectionIsMandatory(true);
      throw new Error(t('train-ai.fact-checking.error.corrected-url-invalid'));
    }

    // If the status is needs_improvement and the report issue is true, the report issue categories are required.
    if (reportIssue && !reportIssueParams.categories.length) {
      scrollToReportIssueSection();
      throw new Error(
        t('train-ai.fact-checking.report-issue.category.required'),
      );
    }
  }, [
    correctedAnswer,
    correctedURL,
    reportIssue,
    reportIssueParams,
    t,
    scrollToReportIssueSection,
  ]);

  const saveChanges = useCallback(async () => {
    const currentConversation = conversations.results[currentIndex];

    // If the new update request fields are the same as the current conversation's check result fields, don't call the API.
    if (
      !hasChanges(currentConversation, {
        status,
        correctedAnswer,
        correctedURL,
        reportIssue,
        reportIssueParams,
        useForAIKnowledgeData,
        newRequestedStatus,
      })
    ) {
      return; // No changes, so don't call the API
    }

    if (!status) {
      return;
    }

    if (
      typeof isReoptimization === 'undefined' &&
      (currentConversation?.checkResult?.status ||
        currentConversation.isEvaluable === false)
    ) {
      isReoptimization = true; // If the conversation is not evaluable ( because same question is already evaluated ), we are in reoptimization mode.
    }

    // Save the changes, if the status is "Good" and the correctedAnswer is empty, or the status is "Needs improvement" and the correctedAnswer ( and URL if it is exists ) is/are not empty.
    const response = await updateCheckResult(
      currentConversation._id,
      status,
      correctedAnswer || '',
      correctedURL || '',
      reportIssue,
      reportIssue ? reportIssueParams : undefined,
      useForAIKnowledgeData,
      isReoptimization,
      llmBlockId,
      requestedHotTopicId,
    );

    // If the "Report Issue" toggle is enabled, after submitting the report by clicking the Save button on the "Fact-checking AI answers" modal,
    // consider displaying a confirmation message to inform the user that the report was successfully submitted.
    if (response?.reportIssue) {
      toastInfoTranslated(
        'train-ai.fact-checking.report-issue.submitted-successfully',
      );
    }

    if (typeof response !== 'undefined') {
      const translationFields = translationState
        ? {
            translation: {
              question: translationState.translatedQuestion,
              answer: translationState.translatedAnswer,
              sourceLang: translationState.sourceLang,
            },
          }
        : undefined;
      updateCheckResultFieldInState(
        setConversations,
        currentConversation._id,
        response,
        isReoptimization,
        llmBlockId,
        undefined,
        translationFields,
      );
      updatedConversation(response, translationFields);
    }
  }, [
    conversations,
    status,
    correctedAnswer,
    correctedURL,
    reportIssue,
    reportIssueParams,
    useForAIKnowledgeData,
    currentIndex,
    updateCheckResult,
    updatedConversation,
    newRequestedStatus,
    hasChanges,
    isReoptimization,
    llmBlockId,
    requestedHotTopicId,
    translationState,
  ]);

  // Ensure the parent always refreshes its data after the modal closes,
  // so example: counters and percentage bars update immediately after reviewing questions.
  // This calls the callback prop on unmount, regardless of how the modal was closed.
  useEffect(() => {
    return () => {
      // Don't call callback if:
      // 1. We have preserved state (means we're returning from another modal)
      // 2. We're navigating to another modal
      if (
        callback &&
        !localPreservedState &&
        !isNavigatingToAnotherModal.current
      ) {
        callback();
      }
    };
  }, [callback, localPreservedState]);

  const clearTemplateSelection = useCallback(() => {
    setClearTrigger((previousTrigger) => previousTrigger + 1); // Increment to trigger clear
  }, []);

  const handleSaveProcess = useCallback(
    async (afterSaveCallback?: () => void) => {
      try {
        // The user can go to the next question without selecting a status, so skip the item.
        // An intent redirect question has nothing to save either.
        if (!status || isCurrentConversationIntentRedirect) {
          if (afterSaveCallback) {
            afterSaveCallback();
          }
          return;
        }

        if (status === LLMUsageCheckResultStatus.NEEDS_IMPROVEMENT) {
          validateNeedsImprovement();
        }

        // Check if "Save as template" is enabled
        if (
          saveAnswerAsTemplate &&
          status === LLMUsageCheckResultStatus.NEEDS_IMPROVEMENT
        ) {
          if (!templateName?.trim()) {
            toastErrorTranslated('modals.save-as-template.name-required');
            return;
          }

          await createTemplateResponse({
            name: templateName.trim(),
            content: correctedAnswer?.trim(),
            url: correctedURL?.replace(URL_REMOVAL_FLAG, '')?.trim(),
          });

          // Clear the template name after successful save
          setTemplateName('');
          setSaveAnswerAsTemplate(false);
        }

        // Continue with the normal save process
        await saveChanges();

        hasAppliedPreservedStateRef.current = false;

        // Clear template selection after successful save
        clearTemplateSelection();

        if (afterSaveCallback) {
          afterSaveCallback();
        }
      } catch (error: any) {
        if (error?.response?.data?.message === ErrorCodes.ALREADY_EXISTS) {
          return;
        }

        if (error instanceof Error) {
          toastErrorTranslated(error?.message);
        }
      }
    },
    [
      saveChanges,
      saveAnswerAsTemplate,
      correctedAnswer,
      status,
      isCurrentConversationIntentRedirect,
      templateName,
      createTemplateResponse,
      correctedURL,
      validateNeedsImprovement,
      clearTemplateSelection,
    ],
  );

  const handleNext = useCallback(async () => {
    await handleSaveProcess(() => {
      if (currentIndex < conversations?.results?.length - 1) {
        setCurrentIndex(currentIndex + 1);
      }
    });
  }, [currentIndex, conversations, handleSaveProcess]);

  const handlePrevious = useCallback(async () => {
    await handleSaveProcess(() => {
      if (currentIndex > 0) {
        setCurrentIndex(currentIndex - 1);
      }
    });
  }, [currentIndex, handleSaveProcess]);

  const hideAndSave = useCallback(async () => {
    await handleSaveProcess(() => {
      if (callback) {
        callback();
      }
      hideModal();
    });
  }, [handleSaveProcess, hideModal, callback]);

  // Scroll to the report issue section when the toggle is clicked
  useEffect(() => {
    if (reportIssue && isUserInteraction) {
      scrollToReportIssueSection();
    }
  }, [reportIssue, isUserInteraction, scrollToReportIssueSection]);

  const handleTemplateResponseSelect = useCallback(
    (_name: string, responseId: string) => {
      // Find the selected template response and auto-fill the corrected answer and/or URL
      const selectedResponse = templateResponses.find(
        (response) => response._id.toString() === responseId,
      );

      if (selectedResponse) {
        setCorrectedAnswer(selectedResponse.content || '');
        setCorrectedURL(selectedResponse.url || '');
        answerEditorRef.current?.setEditorText(selectedResponse.content || '');
      }
    },
    [templateResponses],
  );

  const handleHotTopicLabelClick = useCallback(async () => {
    const topicMetadata = currentConversation?.hotTopicMetadata;
    if (!topicMetadata?.id) {
      return;
    }

    isNavigatingToAnotherModal.current = true;

    await handleViewRelatedConversations(
      {
        id: topicMetadata.id,
        name: topicMetadata.name,
        percentage: topicMetadata.percentage,
      },
      () => {
        showModal('train-ai-fact-checking', '', {
          data: conversations,
          newRequestedStatus,
          updatedConversation,
          onTranslated,
          callback,
          isReoptimization,
          preservedState: {
            currentIndex,
            status,
            correctedAnswer,
            correctedURL,
            reportIssue,
            reportIssueParams,
            useForAIKnowledgeData,
            saveAnswerAsTemplate,
            templateName,
          },
        });
      },
    );
  }, [
    currentConversation,
    handleViewRelatedConversations,
    conversations,
    currentIndex,
    status,
    correctedAnswer,
    correctedURL,
    reportIssue,
    reportIssueParams,
    useForAIKnowledgeData,
    saveAnswerAsTemplate,
    templateName,
    newRequestedStatus,
    updatedConversation,
    onTranslated,
    callback,
    isReoptimization,
    showModal,
  ]);

  return (
    <form className='tailwind-modal-form fact-checking-modal'>
      <div className='tailwind-modal-body fact-checking-modal__body'>
        {conversations?.results?.length > 0 ? (
          <>
            <div className='fact-checking-modal__container'>
              {/* Header section */}
              <div className='fact-checking-modal__header grid grid-cols-[1fr_auto_1fr]'>
                <div className='flex items-center'>
                  {conversations?.totalRecords > 1 && (
                    <>
                      <h3 className='fact-checking-modal__header-counter'>
                        #{currentIndex + 1}
                      </h3>
                      <div className='fact-checking-modal__nav'>
                        <GeneralTooltip
                          text={t('general.buttons.previous-and-save')}
                          placement='bottom'>
                          <MaterialIcon
                            icon='arrow_back_ios'
                            classes={['fact-checking-modal__nav-icon']}
                            onClick={handlePrevious}
                          />
                        </GeneralTooltip>

                        <GeneralTooltip
                          text={t('general.buttons.next-and-save')}
                          placement='bottom'>
                          <MaterialIcon
                            icon='arrow_forward_ios'
                            classes={['fact-checking-modal__nav-icon']}
                            onClick={handleNext}
                          />
                        </GeneralTooltip>
                      </div>
                    </>
                  )}
                </div>
                <div className='fact-checking-modal__status-icons'>
                  {!isCurrentConversationIntentRedirect &&
                    Object.entries(STATUSES).map(([statusKey, { icon }]) => (
                      <i
                        key={statusKey}
                        className={`leading-[normal] select-none not-italic ${statusKey === status ? 'opacity-100' : 'opacity-40'}`}>
                        <img
                          src={iconsHelper[icon]}
                          alt={status}
                          className='inline-flex w-5 h-5 items-center'
                        />
                      </i>
                    ))}
                </div>
                <div className='flex items-center justify-end'>
                  {hasMultilingualTrainAI &&
                    !isCurrentConversationDutch &&
                    !isCurrentConversationIntentRedirect && (
                      <div className='flex items-center gap-1'>
                        {translationState && (
                          <button
                            type='button'
                            onClick={() => {
                              if (showTranslated) {
                                handleTranslate();
                              }
                            }}
                            disabled={isTranslating}
                            title={t(
                              'train-ai.conversation-review.language.view-original',
                              { language: translationState.sourceLanguageName },
                            )}
                            aria-label={t(
                              'train-ai.conversation-review.language.view-original',
                              { language: translationState.sourceLanguageName },
                            )}
                            className={`text-xl leading-none rounded p-0.5 transition-opacity cursor-pointer ${!showTranslated ? 'opacity-100 ring-1 ring-blue-400' : 'opacity-40 hover:opacity-70'}`}>
                            <FlagEmoji
                              languageCode={translationState.sourceLang}
                              size={20}
                            />
                          </button>
                        )}
                        {translationState && (
                          <MaterialIcon
                            icon='sync_alt'
                            classes={['text-gray-400', 'text-xs', 'mx-0.5']}
                          />
                        )}
                        <button
                          type='button'
                          onClick={() => {
                            if (!showTranslated) {
                              handleTranslate();
                            }
                          }}
                          disabled={isTranslating}
                          title={t(
                            'train-ai.conversation-review.language.view-dutch',
                          )}
                          aria-label={t(
                            'train-ai.conversation-review.language.view-dutch',
                          )}
                          className={`text-xl leading-none rounded p-0.5 transition-opacity cursor-pointer ${showTranslated ? 'opacity-100 ring-1 ring-blue-400' : 'opacity-40 hover:opacity-70'}`}>
                          {isTranslating ? (
                            <MaterialIcon icon='sync' classes={['!text-xl']} />
                          ) : (
                            <FlagEmoji languageCode='nl' size={20} />
                          )}
                        </button>
                      </div>
                    )}
                </div>
              </div>
              <div className='fact-checking-modal__content'>
                {/* Question Section */}
                <div className='fact-checking-modal__section fact-checking-modal__section--question'>
                  <Disclosure
                    as='div'
                    defaultOpen={true}
                    className='fact-checking-modal__section--container'>
                    {({ open }) => (
                      <>
                        <DisclosureButton className='fact-checking-modal__section-button'>
                          <div className='fact-checking-modal__section-button-content'>
                            <MaterialIcon
                              icon='play_arrow'
                              classes={[
                                'fact-checking-modal__section-button-content--icon',
                                open
                                  ? 'transform rotate-90'
                                  : 'transform rotate-[-90deg]',
                              ]}
                            />
                            <h3 className='fact-checking-modal__section-button-content--text'>
                              {t('train-ai.fact-checking.user-question')}
                            </h3>
                          </div>
                          {currentConversation?.hotTopicMetadata && (
                            <div
                              className='fact-checking-modal__section-button-content__hot-topics-label'
                              onClick={(event) => {
                                event.stopPropagation();
                                handleHotTopicLabelClick();
                              }}>
                              {getHotTopicDisplayName(
                                currentConversation.hotTopicMetadata.name,
                              )}
                            </div>
                          )}
                        </DisclosureButton>
                        <DisclosurePanel className='fact-checking-modal__section-content'>
                          <div className='fact-checking-modal__section-content--text fact-checking-modal__question-text'>
                            {showTranslated && translationState
                              ? translationState.translatedQuestion
                              : currentConversation?.question}
                          </div>
                        </DisclosurePanel>
                      </>
                    )}
                  </Disclosure>
                </div>

                {/* AI Answer Section */}
                <div className='fact-checking-modal__section fact-checking-modal__section--answer'>
                  <Disclosure
                    as='div'
                    defaultOpen={true}
                    className='fact-checking-modal__section--container'>
                    {({ open }) => (
                      <>
                        <DisclosureButton className='fact-checking-modal__section-button'>
                          <MaterialIcon
                            icon='play_arrow'
                            classes={[
                              'fact-checking-modal__section-button-content--icon',
                              open
                                ? 'transform rotate-90'
                                : 'transform rotate-[-90deg]',
                            ]}
                          />
                          <h3 className='fact-checking-modal__section-button--text'>
                            {t('train-ai.fact-checking.answer')}
                          </h3>
                        </DisclosureButton>
                        <DisclosurePanel className='fact-checking-modal__section-content'>
                          {isCurrentConversationIntentRedirect ? (
                            <div className='fact-checking-modal__section-content--text'>
                              <IntentRedirectLabel />
                            </div>
                          ) : (
                            <div className='fact-checking-modal__section-content--text fact-checking-modal__answer-text formatted-html-content'>
                              {parse(
                                purify(
                                  showTranslated && translationState
                                    ? translationState.translatedAnswer
                                    : currentConversation?.answer || '',
                                ),
                              )}
                            </div>
                          )}
                        </DisclosurePanel>
                      </>
                    )}
                  </Disclosure>
                </div>

                {/* URL Section */}
                {currentConversation?.url?.trim() && (
                  <div className='fact-checking-modal__section fact-checking-modal__section--url'>
                    <Disclosure
                      as='div'
                      defaultOpen={true}
                      className='fact-checking-modal__section--container'>
                      {({ open }) => (
                        <>
                          <DisclosureButton className='fact-checking-modal__section-button'>
                            <MaterialIcon
                              icon='play_arrow'
                              classes={[
                                'fact-checking-modal__section-button--icon',
                                open
                                  ? 'transform rotate-90'
                                  : 'transform rotate-[-90deg]',
                              ]}
                            />
                            <h3 className='fact-checking-modal__section-button--text'>
                              {t('train-ai.fact-checking.url')}
                            </h3>
                          </DisclosureButton>
                          <DisclosurePanel className='fact-checking-modal__section-content'>
                            <div
                              className='fact-checking-modal__section-content--text fact-checking-modal__url-text'
                              onClick={() =>
                                window.open(
                                  conversations.results[currentIndex].url,
                                  '_blank',
                                )
                              }>
                              {conversations.results[currentIndex].url}
                            </div>
                          </DisclosurePanel>
                        </>
                      )}
                    </Disclosure>
                  </div>
                )}
              </div>
            </div>

            {/* Status Section */}
            {!isCurrentConversationIntentRedirect && (
              <FactCheckingStatusSection
                statuses={STATUSES}
                status={status}
                setStatus={handleStatusChange}
              />
            )}
            {/* Correction Section */}
            {shouldDisplayCorrectionSection &&
              !isCurrentConversationIntentRedirect && (
                <div
                  className={`fact-checking-modal__correction ${
                    correctionIsMandatory
                      ? 'fact-checking-modal__correction--mandatory'
                      : ''
                  }`}>
                  {/* Template Response Section */}
                  {hasPermissions([
                    PERMISSIONS.TEMPLATE_RESPONSE.TEMPLATE_RESPONSE_LIST,
                  ]) &&
                    templateResponseValues.length > 0 && (
                      <div className='fact-checking-modal__template-response-section'>
                        <DropDownSearchInput
                          horizontal
                          label={t('common.template-responses')}
                          placeholder={t(
                            'train-ai.fact-checking.select-template-response',
                          )}
                          name='templateResponse'
                          values={templateResponseValues}
                          setValue={handleTemplateResponseSelect}
                          clearTrigger={clearTrigger}
                          isEnabled={true}
                          labelClassName='leading-tight truncate font-medium'
                          parentClassName='flex items-center'
                          inputClassName='text-xs leading-0 rounded-sm'
                          dropdownItemClassName='text-xs'
                        />
                      </div>
                    )}

                  {/* Answer correction */}
                  <div className='fact-checking-modal__correction-section'>
                    <Disclosure as='div' defaultOpen={true}>
                      {({ open }) => (
                        <>
                          <DisclosureButton className='fact-checking-modal__correction-button'>
                            <div className='fact-checking-modal__correction-button-content'>
                              <MaterialIcon
                                icon='play_arrow'
                                classes={[
                                  'fact-checking-modal__correction-button--icon',
                                  open
                                    ? 'transform rotate-90'
                                    : 'transform rotate-[-90deg]',
                                ]}
                              />
                              <h3 className='fact-checking-modal__section-button--text'>
                                {t('train-ai.fact-checking.write-correction')}
                              </h3>
                            </div>
                            {/*  The reviewer icon should be only displayed for the 'Needs improvement' status */}
                            {currentConversation?.checkResult?.status ===
                              LLMUsageCheckResultStatus.NEEDS_IMPROVEMENT &&
                              currentConversation?.checkResult
                                ?.checkedByName && (
                                <GeneralTooltip
                                  text={t('train-ai.fact-checking.checked-by', {
                                    name: currentConversation?.checkResult
                                      ?.checkedByName,
                                    date: formatDate(
                                      currentConversation?.checkResult
                                        ?.checkedAt,
                                    ),
                                  })}
                                  isHtmlText={true}
                                  placement='left'
                                  className={`right-7 !leading-tight !text-timestamp ${currentConversation?.checkResult?.checkedAt ? 'top-[12px]' : ''}`}>
                                  <img
                                    src={person}
                                    alt={t(
                                      'train-ai.fact-checking.checked-by-icon',
                                    )}
                                    className='fact-checking-modal__correction-button-checked-by'
                                  />
                                </GeneralTooltip>
                              )}
                          </DisclosureButton>
                          <DisclosurePanel className='flex-grow overflow-y-auto'>
                            <div className='fact-checking-modal__textarea-container'>
                              <AnswerEditor
                                ref={answerEditorRef}
                                value={correctedAnswer}
                                onChange={handleCorrectedAnswerChange}
                                constantValue={currentConversation?._id ?? ''}
                                templateOptions={templateOptions}
                                onTemplateSelect={setCorrectedURL}
                              />
                            </div>
                          </DisclosurePanel>
                        </>
                      )}
                    </Disclosure>
                  </div>

                  {/* URL correction / Adding a URL (optional) */}
                  <div className='fact-checking-modal__correction-section'>
                    <Disclosure as='div' defaultOpen={true}>
                      {({ open }) => (
                        <>
                          <DisclosureButton className='fact-checking-modal__correction-button fact-checking-modal__correction-button--url'>
                            <MaterialIcon
                              icon='play_arrow'
                              classes={[
                                'fact-checking-modal__correction-button--icon',
                                open
                                  ? 'transform rotate-90'
                                  : 'transform rotate-[-90deg]',
                              ]}
                            />
                            <h3 className='fact-checking-modal__section-button--text'>
                              {currentConversation?.url
                                ? t(
                                    'train-ai.fact-checking.write-correction-url',
                                  )
                                : t('train-ai.fact-checking.add-source-url')}
                            </h3>
                            <GeneralTooltip
                              text={t('train-ai.fact-checking.url-info')}
                              placement='left'
                              className='fact-checking-modal__correction-button--tooltip'>
                              <MaterialIcon
                                icon='info'
                                classes={[
                                  'fact-checking-modal__correction-button--info',
                                ]}
                              />
                            </GeneralTooltip>
                          </DisclosureButton>
                          <DisclosurePanel className='flex-grow overflow-y-auto'>
                            <TextareaT
                              field='correctedURL'
                              parentClassName='!mb-0'
                              className='fact-checking-modal__textarea fact-checking-modal__textarea--url'
                              rows={1}
                              placeholder='https:// or http://'
                              value={correctedURL?.replace(
                                URL_REMOVAL_FLAG,
                                '',
                              )}
                              onChange={handleCorrectedURLChange}
                            />
                          </DisclosurePanel>
                        </>
                      )}
                    </Disclosure>
                  </div>

                  <div className='fact-checking-modal__report-issue-toggler-section'>
                    <CheckboxTogglerT
                      className={`report-issue-toggle ${
                        reportIssue
                          ? 'report-issue-toggle--expanded'
                          : 'report-issue-toggle--collapsed'
                      }`}
                      label={t('train-ai.fact-checking.report-issue')}
                      labelClassName='text-primary-base text-xs'
                      colorScheme='red'
                      toggleSize='small'
                      checked={reportIssue}
                      onChange={(event) => {
                        setReportIssue(event.target.checked);
                        setIsUserInteraction(true);
                      }}
                    />

                    {reportIssue && (
                      <>
                        <div className='tailwind-divider-x w-full border-red-base opacity-50' />

                        <div
                          id='report-issue-section'
                          className='fact-checking-modal__report-issue-section'>
                          <FactCheckingReportIssueSection
                            reportIssueParams={reportIssueParams}
                            setReportIssueParams={(params) =>
                              setReportIssueParams(params)
                            }
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
          </>
        ) : (
          <div className='fact-checking-modal__empty'>
            <h3>{t('train-ai.fact-checking.no-conversations')}</h3>
          </div>
        )}
      </div>
      <div className='tailwind-modal-footer fact-checking-modal__footer'>
        {conversations?.results?.length > 0 &&
        !isCurrentConversationIntentRedirect ? (
          <div className='fact-checking-modal__footer-toggler-section'>
            <div className='fact-checking-modal__footer-toggles'>
              {hasPermissions([
                PERMISSIONS.TEMPLATE_RESPONSE.TEMPLATE_RESPONSE_CREATE,
              ]) &&
                status === LLMUsageCheckResultStatus.NEEDS_IMPROVEMENT && (
                  <CheckboxTogglerT
                    label={t('train-ai.fact-checking.save-as-template')}
                    labelClassName='text-primary-base text-sm'
                    colorScheme='default'
                    toggleSize='base'
                    checked={saveAnswerAsTemplate}
                    onChange={(event) => {
                      setSaveAnswerAsTemplate(event.target.checked);
                      // Clear template name when unchecking
                      if (!event.target.checked) {
                        setTemplateName('');
                      }
                    }}
                  />
                )}
              <CheckboxTogglerT
                label={t('train-ai.fact-checking.use-for-ai-knowledge-data')}
                labelClassName='text-primary-base text-sm'
                colorScheme='default'
                toggleSize='base'
                checked={useForAIKnowledgeData}
                onChange={(event) => {
                  setUseForAIKnowledgeData(event.target.checked);
                }}
                options={{
                  disabled: status === LLMUsageCheckResultStatus.IRRELEVANT,
                }}
              />
            </div>
            {/* Template Name Input - shown when save as template is checked */}
            {saveAnswerAsTemplate && (
              <div className='fact-checking-modal__template-name-input'>
                <LabelT text={t('modals.save-as-template.name-input')} />
                <InputT
                  type='text'
                  options={{
                    value: templateName,
                    onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                      setTemplateName(event.target.value),
                  }}
                  placeholder={t('modals.save-as-template.name-input')}
                  maxLength={100}
                  className='text-xs'
                />
              </div>
            )}
          </div>
        ) : (
          ''
        )}
        <div className='fact-checking-modal__footer-buttons'>
          <ButtonT
            onClick={hideModal}
            type='button'
            styling='cancel'
            size='xs'
            rounding='md'>
            {t('general.buttons.cancel')}
          </ButtonT>
          {conversations?.results?.length > 0 && (
            <ButtonT
              type='button'
              styling='primary'
              size='xs'
              rounding='md'
              onClick={hideAndSave}>
              {t('general.buttons.save')}
            </ButtonT>
          )}
        </div>
      </div>
    </form>
  );
};

export default FactCheckingModal;
